import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  PropsWithChildren,
  useCallback,
  useMemo,
} from "react";
import {
  useAudioVideo,
  useRosterState,
  useToggleLocalMute,
  useLocalVideo,
  useContentShareState,
} from "amazon-chime-sdk-component-library-react";
import TranscriptEvent from "amazon-chime-sdk-js/build/transcript/TranscriptEvent";
import Transcript from "amazon-chime-sdk-js/build/transcript/Transcript";
import { extractRoomMemberData } from "@core/features/video";
import type { TranscriptionSegment } from "@core/features/video";
import { useVideoMeetingDetails, useVideoMeetingTranscription, useVideoSession, useUser, usePlatformStore } from "@platform/store";
import { toggleVideoMeetingTranscription, useVideoMeetingHeartbeat, logMeetingEvent } from "@core/features/videoMeeting";
import type { VideoRoomHeartbeatResponse, DeclaredVideoState } from "@core/features/videoMeeting";
import { collectCallQualityMetrics } from "@core/features/videoMeeting/utils/collectCallQualityMetrics";
import { logVideoRoomError } from "../utils/logVideoRoomError";

export interface TranscriptionState {
  isTranscriptionDisabled: boolean;
  isTranscribing: boolean;
  segments: TranscriptionSegment[];
  currentSegment: TranscriptionSegment | null;
  lines: string[]; // For displaying transcript lines
  isLoading: boolean; // Loading state when starting transcription
  error: string | null; // Error state for transcription operations
}

interface TranscriptionContextType {
  state: TranscriptionState;
  toggleTranscription: () => void;
  isTranscriptionAvailable: boolean; // Whether transcription can be toggled
}

const TranscriptionContext = createContext<
  TranscriptionContextType | undefined
>(undefined);

/** Minimum confidence score for a transcript result to be accepted */
const TRANSCRIPTION_CONFIDENCE_THRESHOLD = 0.5;

/** Debounce interval for partial transcript updates (ms) */
const TRANSCRIPTION_PARTIAL_DEBOUNCE_MS = 300;

const initialState: TranscriptionState = {
  isTranscriptionDisabled: false,
  isTranscribing: false,
  segments: [],
  currentSegment: null,
  lines: [],
  isLoading: false,
  error: null,
};

export const TranscriptionProvider: React.FC<
  PropsWithChildren<{
    isTranscriptionDisabled: boolean;
  }>
> = ({ children, isTranscriptionDisabled }) => {
  const audioVideo = useAudioVideo();
  const { roster } = useRosterState();
  const { meetingId, roomId, meetingDetails } = useVideoMeetingDetails();
  const { transcriptionStatus, setTranscriptionStatus } = useVideoMeetingTranscription();
  const { userName } = useVideoSession();
  const user = useUser();
  const connectionQuality = usePlatformStore((s) => s.connectionQuality);
  const [state, setState] = useState<TranscriptionState>(initialState);
  const isToggling = useRef(false);

  // Declared-state sources from the Chime SDK React hooks. All three
  // return primitive booleans backed by useState/context, so useEffect
  // dependency changes fire on transition (see docs/video-architecture.md
  // §10). Used by getDeclaredState (below) to inform the heartbeat, and
  // by the three transition-watching effects that emit intent_change.
  const { muted } = useToggleLocalMute();
  const { isVideoEnabled } = useLocalVideo();
  const { isLocalUserSharing } = useContentShareState();

  // Keep refs for values needed inside the metrics callback
  const connectionQualityRef = useRef(connectionQuality);
  connectionQualityRef.current = connectionQuality;
  const rosterRef = useRef(roster);
  rosterRef.current = roster;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const isVideoEnabledRef = useRef(isVideoEnabled);
  isVideoEnabledRef.current = isVideoEnabled;
  const isLocalUserSharingRef = useRef(isLocalUserSharing);
  isLocalUserSharingRef.current = isLocalUserSharing;
  // audioVideo is passed through to collectCallQualityMetrics for
  // getStats enrichment (qualityLimitationReason etc). Kept in a ref so
  // facade identity changes across reconnects don't reset the heartbeat
  // hook's useEffect.
  const audioVideoRef = useRef(audioVideo);
  audioVideoRef.current = audioVideo;

  const getCallQualityMetrics = useCallback(
    async () => {
      // Resolve device labels lazily from store + enumerateDevices.
      // Does NOT subscribe to DevicesProvider (which causes devicechange races).
      let audioLabel: string | null = null;
      let videoLabel: string | null = null;
      try {
        const s = usePlatformStore.getState();
        const audioId = s.selectedAudioInputDevice;
        const videoId = s.selectedVideoInputDevice;
        if (audioId || videoId) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (audioId) audioLabel = devices.find(d => d.deviceId === audioId)?.label ?? null;
          if (videoId) videoLabel = devices.find(d => d.deviceId === videoId)?.label ?? null;
        }
      } catch { /* non-critical */ }

      // Reads from ChimeObserverState's metricsDidReceive ref (cross-browser,
      // sync baseline) and enriches with RTCPeerConnection.getStats() for
      // the fields the SDK observable surface doesn't expose
      // (qualityLimitationReason, inbound jitter/framesDropped). See the
      // utility's docstring for the per-browser feature matrix.
      return collectCallQualityMetrics(
        {
          connectionQuality: connectionQualityRef.current,
          attendeeCount: Object.keys(rosterRef.current).length,
          missedHeartbeats: 0,
          resumedFromBackground: false,
          activeAudioInputLabel: audioLabel,
          activeVideoInputLabel: videoLabel,
        },
        audioVideoRef.current,
      );
    },
    []
  );

  const handleHeartbeat = useCallback(
    (response: VideoRoomHeartbeatResponse) => {
      setTranscriptionStatus(response.transcription_status);
    },
    [setTranscriptionStatus]
  );

  // Snapshot the current declared-state values for each heartbeat tick.
  // Closes over refs (not props) so the callback identity is stable
  // across renders and the heartbeat hook's useEffect doesn't restart.
  const getDeclaredState = useCallback(
    (): DeclaredVideoState => ({
      mic_intent: mutedRef.current ? "off" : "on",
      camera_intent: isVideoEnabledRef.current ? "on" : "off",
      is_screen_sharing: isLocalUserSharingRef.current,
      connection_quality: connectionQualityRef.current,
    }),
    []
  );

  // The heartbeat hook lists `user` in its useEffect deps. Passing a fresh
  // `{ id: user.id }` literal here would change reference on every render of
  // this provider (frequent during a live meeting) and tear down + restart
  // the 10s interval each time, firing an immediate heartbeat per render.
  // Memoize so the reference only changes when the underlying id changes.
  const heartbeatUser = useMemo(
    () => (user ? { id: user.id } : null),
    [user?.id],
  );

  useVideoMeetingHeartbeat({
    meetingId: meetingId ? String(meetingId) : null,
    userName,
    user: heartbeatUser,
    isEnabled: Boolean(meetingId),
    onHeartbeat: handleHeartbeat,
    getCallQualityMetrics,
    getDeclaredState,
    resumeOnForeground: true,
  });

  // Intent-transition observers. Each fires a best-effort intent_change
  // event on the user hash whenever the underlying Chime value flips.
  // The first-render + no-change guard (prev === null || prev === current)
  // covers both initial mount and React StrictMode's mount → cleanup →
  // mount double-invoke. See docs/video-architecture.md §17.
  //
  // from/to always use the on-hash representation of the field: "on"/"off"
  // for mic/camera, "0"/"1" for screen share. connection_quality does NOT
  // emit transition events (flap avoidance during network blips; the
  // current-value field on the hash is sufficient).
  const prevMutedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevMutedRef.current === null || prevMutedRef.current === muted) {
      prevMutedRef.current = muted;
      return;
    }
    logMeetingEvent({
      event_type: "intent_change",
      meeting_id: meetingId ?? null,
      room_id: roomId ?? null,
      user: user ? { id: user.id } : null,
      field: "mic_intent",
      from: prevMutedRef.current ? "off" : "on",
      to: muted ? "off" : "on",
    });
    prevMutedRef.current = muted;
  }, [muted, meetingId, roomId, user]);

  const prevVideoEnabledRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevVideoEnabledRef.current === null || prevVideoEnabledRef.current === isVideoEnabled) {
      prevVideoEnabledRef.current = isVideoEnabled;
      return;
    }
    logMeetingEvent({
      event_type: "intent_change",
      meeting_id: meetingId ?? null,
      room_id: roomId ?? null,
      user: user ? { id: user.id } : null,
      field: "camera_intent",
      from: prevVideoEnabledRef.current ? "on" : "off",
      to: isVideoEnabled ? "on" : "off",
    });
    prevVideoEnabledRef.current = isVideoEnabled;
  }, [isVideoEnabled, meetingId, roomId, user]);

  const prevScreenSharingRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevScreenSharingRef.current === null || prevScreenSharingRef.current === isLocalUserSharing) {
      prevScreenSharingRef.current = isLocalUserSharing;
      return;
    }
    logMeetingEvent({
      event_type: "intent_change",
      meeting_id: meetingId ?? null,
      room_id: roomId ?? null,
      user: user ? { id: user.id } : null,
      field: "is_screen_sharing",
      from: prevScreenSharingRef.current ? "1" : "0",
      to: isLocalUserSharing ? "1" : "0",
    });
    prevScreenSharingRef.current = isLocalUserSharing;
  }, [isLocalUserSharing, meetingId, roomId, user]);

  const lastPartialText = useRef<string | null>(null);
  const lastPartialUpdate = useRef<number>(0);

  // Check if transcription is available (meeting exists and user has access)
  const isTranscriptionAvailable = Boolean(meetingDetails?.id);

  // `transcriptionStatus` is meeting-wide backend service state (synced via heartbeat).
  // Each user's caption *display* is local to this provider — see `state.isTranscribing`.
  const isBackendTranscribing = transcriptionStatus === 'on';

  useEffect(() => {
    // Clean up local display when the backend service stops (expiry / cleanup task).
    // Never auto-enable: starting the service does not mean this user opted in.
    if (!isBackendTranscribing && state.isTranscribing) {
      setState(prev => ({
        ...prev,
        isTranscribing: false,
        segments: [],
        currentSegment: null,
        lines: [],
        error: null,
      }));
      lastPartialText.current = null;
      lastPartialUpdate.current = 0;
    }
  }, [isBackendTranscribing, state.isTranscribing]);

  // Subscribe to transcription events using the Chime SDK
  useEffect(() => {
    // If transcription is not enabled or not transcribing, don't subscribe to transcription events
    if (isTranscriptionDisabled || !state.isTranscribing) {
      return;
    }

    if (!audioVideo) {
      return;
    }

    // Check if transcription controller is available
    if (audioVideo.transcriptionController) {
      const transcriptCallback = (transcriptEvent: TranscriptEvent) => {
        if (!(transcriptEvent instanceof Transcript)) return;
        if (!transcriptEvent.results || transcriptEvent.results.length === 0) return;

        const result = transcriptEvent.results[0];
        const alternative = result.alternatives?.[0];
        if (!alternative) return;
        const text = alternative.transcript.trim();
        const attendee = alternative.items?.[0]?.attendee || null;
        const speakerId = attendee?.externalUserId || "unknown";
        const { displayName: speakerName } = attendee
          ? extractRoomMemberData(attendee)
          : { displayName: "Speaker" };

        const timestamp = Date.now();
        if (!result.isPartial) {
          const confidence =
            alternative.items && alternative.items.length > 0
              ? alternative.items[0].confidence || 0
              : 0;

          if (confidence > TRANSCRIPTION_CONFIDENCE_THRESHOLD && text.length > 0) {
            const newSegment: TranscriptionSegment = {
              id: timestamp.toString(),
              text,
              speakerId,
              speakerName,
              startTime: timestamp,
              endTime: timestamp,
              isPartial: false,
              confidence,
            };

            const line = `${speakerId}: ${text}`;

            setState((prev) => ({
              ...prev,
              currentSegment: newSegment,
              segments: [...prev.segments, newSegment],
              lines: [...prev.lines, line],
            }));

            // Reset partial tracking
            lastPartialText.current = null;
            lastPartialUpdate.current = 0;
          }
        } else {
          // Skip short/empty partials
          if (text.length < 6) return;

          const didChange = text !== lastPartialText.current;
          const now = Date.now();
          const timeSinceLast = now - lastPartialUpdate.current;

          if (didChange && timeSinceLast > TRANSCRIPTION_PARTIAL_DEBOUNCE_MS) {
            lastPartialText.current = text;
            lastPartialUpdate.current = now;

            const partialSegment: TranscriptionSegment = {
              id: `partial-${now}`,
              text,
              speakerId,
              speakerName,
              startTime: now,
              endTime: now,
              isPartial: true,
              confidence: 0,
            };

            setState((prev) => ({
              ...prev,
              currentSegment: partialSegment,
            }));
          }
        }
      };

      // Subscribe to transcript events
      audioVideo.transcriptionController.subscribeToTranscriptEvent(
        transcriptCallback
      );

      return () => {
        // Unsubscribe from transcript events
        if (audioVideo.transcriptionController) {
          audioVideo.transcriptionController.unsubscribeFromTranscriptEvent(
            transcriptCallback
          );
        }
      };
    }
  }, [audioVideo, isTranscriptionDisabled, state.isTranscribing]);

  // Toggle transcription function
  const toggleTranscription = useCallback(async () => {
    if (isToggling.current) return;
    if (!isTranscriptionAvailable || !meetingDetails?.id) {
      setState(prev => ({
        ...prev,
        error: 'Transcription not available for this meeting',
      }));
      return;
    }

    // If this user is currently showing captions, hide them locally.
    if (state.isTranscribing) {
      setState(prev => ({
        ...prev,
        isTranscribing: false,
        error: null,
      }));
    } else {
      // Not showing locally. If the backend service is already running, just show.
      if (transcriptionStatus === 'on') {
        setState(prev => ({
          ...prev,
          isTranscribing: true,
          error: null,
        }));
      } else {
        // Service is off — call the API to start it, then show locally.
        try {
          isToggling.current = true;
          setState(prev => ({ ...prev, isLoading: true, error: null }));
          const response = await toggleVideoMeetingTranscription(meetingDetails.id);

          if (response.transcription_status === 'on') {
            setState(prev => ({
              ...prev,
              isTranscribing: true,
              isLoading: false,
              error: null,
            }));
          } else {
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: 'Transcription started but status is not on',
            }));
          }
        } catch (error) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: 'Failed to turn on transcription',
          }));
          logVideoRoomError(
            "Failed to toggle transcription",
            "transcription_toggle_error",
            { meetingId: meetingDetails?.id, error: String(error) }
          );
        } finally {
          isToggling.current = false;
        }
      }
    }
  }, [isTranscriptionAvailable, meetingDetails?.id, state.isTranscribing, transcriptionStatus]);

  const contextValue: TranscriptionContextType = {
    state: {
      ...state,
      isTranscriptionDisabled,
    },
    toggleTranscription,
    isTranscriptionAvailable,
  };

  return (
    <TranscriptionContext.Provider value={contextValue}>
      {children}
    </TranscriptionContext.Provider>
  );
};

export const useTranscription = (): TranscriptionContextType => {
  const context = useContext(TranscriptionContext);
  if (!context) {
    throw new Error(
      "useTranscription must be used within TranscriptionProvider"
    );
  }
  return context;
};